import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  ExternalLink,
  Clock,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Coins,
  Users,
  Bot,
  Globe,
  Smartphone,
  Shield,
  Timer
} from 'lucide-react';
import { tasksApi, Task, TaskStartResponse } from '../../api';
import { useTelegram } from '../../hooks';

interface TaskModalProps {
  task: Task | null;
  onClose: () => void;
  onComplete: (reward: number, newBalance: number) => void;
}

type TaskStatus = 'idle' | 'starting' | 'in_progress' | 'verifying' | 'completed' | 'failed';

// Task type icons
const TASK_TYPE_ICONS: Record<string, React.ElementType> = {
  telegram_channel: Users,
  telegram_group: Users,
  telegram_bot: Bot,
  telegram_miniapp: Smartphone,
  website: Globe,
  social_like: CheckCircle2,
  social_share: ExternalLink,
  social_visit: Globe,
};

export function TaskModal({ task, onClose, onComplete }: TaskModalProps) {
  const [status, setStatus] = useState<TaskStatus>('idle');
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [taskData, setTaskData] = useState<TaskStartResponse | null>(null);
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);

  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const { hapticFeedback, hapticNotification, openTelegramLink } = useTelegram();

  // Reset state when task changes
  useEffect(() => {
    if (task) {
      setStatus('idle');
      setCountdown(0);
      setError(null);
      setTaskData(null);
      setAttemptsLeft(3);
      setCooldownUntil(null);
    }
  }, [task]);

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  // Handle countdown timer
  useEffect(() => {
    if (status === 'in_progress' && countdown > 0) {
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            if (countdownRef.current) {
              clearInterval(countdownRef.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
        }
      };
    }
  }, [status, countdown]);

  const handleStartTask = async () => {
    if (!task) return;
    
    hapticFeedback('medium');
    setStatus('starting');
    setError(null);

    try {
      const response = await tasksApi.startTask(task.id);

      if (response.success && response.task) {
        setTaskData(response);
        setCountdown(response.task.countdown);
        setStatus('in_progress');

        // Open the target URL
        if (response.task.targetUrl) {
          if (task.type.startsWith('telegram_')) {
            openTelegramLink(response.task.targetUrl);
          } else {
            window.open(response.task.targetUrl, '_blank');
          }
        }
      } else {
        setError(response.message || 'Failed to start task');
        setStatus('failed');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to start task';
      setError(errorMessage);
      setStatus('failed');

      // Check for cooldown
      if (err.response?.data?.cooldownUntil) {
        setCooldownUntil(err.response.data.cooldownUntil);
      }
    }
  };

  const handleVerifyTask = async () => {
    if (!task || !taskData) return;
    
    hapticFeedback('medium');
    setStatus('verifying');
    setError(null);

    try {
      const response = await tasksApi.verifyTask(taskData.task!.taskCompletionId);

      if (response.success) {
        setStatus('completed');
        hapticNotification('success');
        
        // Delay to show success state before closing
        setTimeout(() => {
          onComplete(response.reward!, response.newBalance!);
        }, 1500);
      } else {
        setStatus('in_progress');
        setAttemptsLeft(response.attemptsLeft || attemptsLeft - 1);
        setError(response.message || 'Verification failed');
        hapticNotification('error');

        // If no attempts left, show failed state
        if (response.attemptsLeft === 0) {
          setStatus('failed');
          setCooldownUntil(response.cooldownUntil || null);
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Verification failed';
      setError(errorMessage);
      setStatus('in_progress');
      setAttemptsLeft(prev => prev - 1);
      hapticNotification('error');

      if (err.response?.data?.attemptsLeft === 0) {
        setStatus('failed');
        setCooldownUntil(err.response?.data?.cooldownUntil || null);
      }
    }
  };

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  };

  const formatCooldownTime = (until: number) => {
    const remaining = Math.max(0, until - Date.now());
    const minutes = Math.ceil(remaining / 60000);
    return minutes > 60 ? `${Math.ceil(minutes / 60)} hours` : `${minutes} minutes`;
  };

  if (!task) return null;

  const Icon = TASK_TYPE_ICONS[task.type] || Globe;
  const canVerify = status === 'in_progress' && countdown === 0;
  const isVerifying = status === 'verifying';
  const isCompleted = status === 'completed';
  const isFailed = status === 'failed';

  return (
    <AnimatePresence>
      {task && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            className="fixed bottom-0 left-0 right-0 z-50 max-h-[90vh] overflow-y-auto"
          >
            <div className="bg-surface rounded-t-3xl p-6">
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-white/70" />
              </button>

              {/* Task Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                  {task.thumbnailUrl ? (
                    <img src={task.thumbnailUrl} alt="" className="w-full h-full rounded-2xl object-cover" />
                  ) : (
                    <Icon className="w-8 h-8 text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold text-white">{task.title}</h2>
                  <p className="text-white/60 mt-1">{task.description}</p>
                </div>
              </div>

              {/* Reward Display */}
              <div className="bg-gradient-to-r from-primary/20 to-secondary/20 rounded-xl p-4 mb-6 flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Task Reward</p>
                  <p className="text-2xl font-bold text-primary flex items-center gap-2">
                    <Coins className="w-6 h-6" />
                    {task.reward} points
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white/60">Est. Time</p>
                  <p className="text-lg text-white flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {task.estimatedTime}s
                  </p>
                </div>
              </div>

              {/* Instructions - Blueprint: Type-specific detailed instructions */}
              {status === 'idle' && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
                  <h3 className="font-semibold text-blue-400 mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    How to Complete
                  </h3>
                  <ol className="text-sm text-white/70 space-y-2">
                    {getDetailedInstructions(task.type).map((instruction, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="bg-blue-500/30 text-blue-400 rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
                          {instruction.startsWith('⚠️') ? '!' : index + 1}
                        </span>
                        <span className={instruction.startsWith('⚠️') ? 'text-yellow-400' : ''}>
                          {instruction.replace('⚠️ ', '')}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Progress State */}
              {status === 'in_progress' && (
                <div className="mb-6">
                  {countdown > 0 ? (
                    <div className="text-center">
                      <div className="w-24 h-24 mx-auto rounded-full bg-primary/20 flex items-center justify-center mb-4">
                        <Timer className="w-10 h-10 text-primary" />
                      </div>
                      <p className="text-white/60 mb-2">
                        {task.type.includes('channel') || task.type.includes('group') 
                          ? 'Stay joined while timer counts down...'
                          : 'Complete the action while timer counts...'}
                      </p>
                      <p className="text-4xl font-bold text-white">{formatCountdown(countdown)}</p>
                      <p className="text-xs text-white/40 mt-2">
                        {task.type === 'telegram_channel' || task.type === 'telegram_group'
                          ? 'Membership will be verified automatically'
                          : task.type === 'telegram_bot'
                            ? 'Bot interaction will be verified'
                            : 'Session time is being tracked'}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="w-24 h-24 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                        <CheckCircle2 className="w-10 h-10 text-green-400" />
                      </div>
                      <p className="text-green-400 font-semibold">Ready to verify!</p>
                      <p className="text-sm text-white/60 mt-1">Click the button below to claim your reward</p>
                    </div>
                  )}
                </div>
              )}

              {/* Verifying State */}
              {status === 'verifying' && (
                <div className="mb-6 text-center">
                  <div className="w-24 h-24 mx-auto rounded-full bg-primary/20 flex items-center justify-center mb-4">
                    <Loader2 className="w-10 h-10 text-primary animate-spin" />
                  </div>
                  <p className="text-primary font-semibold">{getVerificationMessage(task.type)}</p>
                  <p className="text-sm text-white/50 mt-1">This may take a few seconds</p>
                </div>
              )}

              {/* Completed State */}
              {isCompleted && (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center mb-6"
                >
                  <div className="w-24 h-24 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', delay: 0.2 }}
                    >
                      <CheckCircle2 className="w-12 h-12 text-green-400" />
                    </motion.div>
                  </div>
                  <p className="text-xl font-bold text-green-400">Task Completed!</p>
                  <p className="text-white/60 mt-1">+{task.reward} points added to your balance</p>
                </motion.div>
              )}

              {/* Error Display */}
              {error && status !== 'completed' && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-400 font-medium">{error}</p>
                    {attemptsLeft > 0 && status === 'in_progress' && (
                      <p className="text-sm text-red-300/70 mt-1">
                        {attemptsLeft} {attemptsLeft === 1 ? 'attempt' : 'attempts'} remaining
                      </p>
                    )}
                    {cooldownUntil && (
                      <p className="text-sm text-red-300/70 mt-1">
                        Please try again in {formatCooldownTime(cooldownUntil)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                {status === 'idle' && (
                  <button
                    onClick={handleStartTask}
                    className="w-full py-4 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold transition-all flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-5 h-5" />
                    Start Task
                  </button>
                )}

                {status === 'starting' && (
                  <button
                    disabled
                    className="w-full py-4 rounded-xl bg-primary/50 text-white/70 font-semibold flex items-center justify-center gap-2"
                  >
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Starting...
                  </button>
                )}

                {status === 'in_progress' && (
                  <>
                    <button
                      onClick={handleVerifyTask}
                      disabled={!canVerify}
                      className={`w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                        canVerify
                          ? 'bg-green-500 hover:bg-green-600 text-white'
                          : 'bg-white/10 text-white/40 cursor-not-allowed'
                      }`}
                    >
                      <CheckCircle2 className="w-5 h-5" />
                      {countdown > 0 ? `Wait ${formatCountdown(countdown)}` : 'Verify & Claim'}
                    </button>

                    {/* Retry opening target */}
                    {taskData?.task?.targetUrl && (
                      <button
                        onClick={() => {
                          if (task.type.startsWith('telegram_')) {
                            openTelegramLink(taskData.task!.targetUrl);
                          } else {
                            window.open(taskData.task!.targetUrl, '_blank');
                          }
                        }}
                        className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white/70 font-medium transition-all flex items-center justify-center gap-2"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Open Again
                      </button>
                    )}
                  </>
                )}

                {isVerifying && (
                  <button
                    disabled
                    className="w-full py-4 rounded-xl bg-green-500/50 text-white/70 font-semibold flex items-center justify-center gap-2"
                  >
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verifying...
                  </button>
                )}

                {isFailed && !cooldownUntil && (
                  <button
                    onClick={() => {
                      setStatus('idle');
                      setError(null);
                      setAttemptsLeft(3);
                    }}
                    className="w-full py-4 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold transition-all"
                  >
                    Try Again
                  </button>
                )}

                {!isCompleted && (
                  <button
                    onClick={onClose}
                    className="w-full py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white/60 font-medium transition-all"
                  >
                    {isFailed ? 'Close' : 'Cancel'}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Get detailed instructions based on task type (Blueprint: user-friendly completion processes)
function getDetailedInstructions(type: string): string[] {
  switch (type) {
    case 'telegram_channel':
      return [
        'Click "Start Task" to open the channel',
        'Join the channel by clicking "Join"',
        'Stay joined for the required time',
        'Return here and click "Verify" to claim reward',
        '⚠️ Do not leave the channel until verified',
      ];
    case 'telegram_group':
      return [
        'Click "Start Task" to open the group',
        'Join the group by clicking "Join"',
        'Stay joined for the required time',
        'Return here and click "Verify" to claim reward',
        '⚠️ Do not leave the group until verified',
      ];
    case 'telegram_bot':
      return [
        'Click "Start Task" to open the bot',
        'Press "START" or send /start to the bot',
        'Wait for confirmation message from bot',
        'Return here and click "Verify" to claim reward',
      ];
    case 'telegram_miniapp':
      return [
        'Click "Start Task" to open the mini app',
        'Explore the app for the required time',
        'Timer will count down automatically',
        'Return here and click "Verify" to claim reward',
      ];
    case 'social_like':
      return [
        'Click "Start Task" to open the post',
        'Like the post by clicking the heart/like button',
        'Wait for the timer to complete',
        'Return here and click "Verify" to claim reward',
      ];
    case 'social_share':
      return [
        'Click "Start Task" to open the content',
        'Share the content on your profile',
        'Wait for the timer to complete',
        'Return here and click "Verify" to claim reward',
      ];
    case 'social_visit':
      return [
        'Click "Start Task" to visit the profile/page',
        'Spend the required time on the page',
        'Timer will count down automatically',
        'Return here and click "Verify" to claim reward',
      ];
    default:
      return [
        'Click "Start Task" to begin',
        'Complete the required action',
        'Wait for the timer to complete',
        'Click "Verify" to claim your reward',
      ];
  }
}

// Get verification status message based on task type
function getVerificationMessage(type: string): string {
  switch (type) {
    case 'telegram_channel':
    case 'telegram_group':
      return 'Checking your membership status...';
    case 'telegram_bot':
      return 'Verifying bot interaction...';
    case 'telegram_miniapp':
      return 'Confirming session duration...';
    default:
      return 'Verifying task completion...';
  }
}
